import { useState, useMemo } from "react";
import PortalLayout from "@/components/PortalLayout";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { toast } from "@/hooks/use-toast";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Star, TrendingUp, MessageSquare } from "lucide-react";
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { Skeleton } from "@/components/ui/skeleton";

const ClientSatisfaction = () => {
  const [surveyRatings, setSurveyRatings] = useState<Record<string, number>>({});

  const { data: surveys = [], isLoading } = useQuery({
    queryKey: ["satisfaction-surveys-portal"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("satisfaction_surveys" as any)
        .select("*")
        .order("submitted_at", { ascending: false });
      if (error) throw error;
      return data as any[];
    },
  });

  const overallScore = useMemo(() => {
    if (!surveys.length) return 0;
    const avg = surveys.reduce((s: number, r: any) => s + (r.rating_overall || 0), 0) / surveys.length;
    return Math.round(avg * 10) / 10;
  }, [surveys]);

  const responseRate = surveys.length > 0 ? Math.min(100, Math.round((surveys.filter((s: any) => s.comments).length / surveys.length) * 100)) : 0;

  const vesselScores = useMemo(() => {
    const map: Record<string, { total: number; count: number }> = {};
    surveys.forEach((s: any) => {
      if (s.vessel_name) {
        if (!map[s.vessel_name]) map[s.vessel_name] = { total: 0, count: 0 };
        map[s.vessel_name].total += s.rating_overall || 0;
        map[s.vessel_name].count += 1;
      }
    });
    return Object.entries(map).map(([vessel, d]) => ({
      vessel,
      score: Math.round((d.total / d.count) * 10) / 10,
    }));
  }, [surveys]);

  // Group surveys by month for trends
  const trends = useMemo(() => {
    const monthMap: Record<string, { overall: number[]; crew: number[]; vessel: number[]; punctuality: number[]; communication: number[]; safety: number[] }> = {};
    surveys.forEach((s: any) => {
      const d = new Date(s.submitted_at);
      const key = d.toLocaleString("en", { month: "short", year: "2-digit" });
      if (!monthMap[key]) monthMap[key] = { overall: [], crew: [], vessel: [], punctuality: [], communication: [], safety: [] };
      if (s.rating_overall) monthMap[key].overall.push(s.rating_overall);
      if (s.rating_crew) monthMap[key].crew.push(s.rating_crew);
      if (s.rating_vessel) monthMap[key].vessel.push(s.rating_vessel);
      if (s.rating_punctuality) monthMap[key].punctuality.push(s.rating_punctuality);
      if (s.rating_communication) monthMap[key].communication.push(s.rating_communication);
      if (s.rating_safety) monthMap[key].safety.push(s.rating_safety);
    });
    const avg = (arr: number[]) => arr.length ? Math.round((arr.reduce((a, b) => a + b, 0) / arr.length) * 10) / 10 : null;
    return Object.entries(monthMap).map(([month, d]) => ({
      month,
      overall: avg(d.overall),
      crew: avg(d.crew),
      vessel: avg(d.vessel),
      punctuality: avg(d.punctuality),
      communication: avg(d.communication),
    }));
  }, [surveys]);

  const handleStarClick = (field: string, rating: number) => {
    setSurveyRatings(prev => ({ ...prev, [field]: rating }));
  };

  const StarRating = ({ field, label }: { field: string; label: string }) => (
    <div className="flex items-center justify-between py-2">
      <span className="text-xs text-[hsl(215,25%,17%)]">{label}</span>
      <div className="flex gap-1">
        {[1,2,3,4,5].map(s => (
          <button key={s} onClick={() => handleStarClick(field, s)} className="p-0.5">
            <Star className={`w-5 h-5 ${(surveyRatings[field] || 0) >= s ? "text-amber-400 fill-amber-400" : "text-gray-200"}`} />
          </button>
        ))}
      </div>
    </div>
  );

  if (isLoading) {
    return (
      <PortalLayout>
        <div className="space-y-4">
          <Skeleton className="h-8 w-64" />
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            {[1,2,3,4].map(i => <Skeleton key={i} className="h-32" />)}
          </div>
        </div>
      </PortalLayout>
    );
  }

  return (
    <PortalLayout>
      <div className="mb-6">
        <p className="text-[11px] font-bold tracking-[3px] uppercase text-[hsl(211,100%,40%)]/60 mb-1">Feedback</p>
        <h1 className="text-3xl font-bold text-[hsl(215,25%,17%)]">Client Satisfaction</h1>
      </div>

      <Tabs defaultValue="dashboard" className="space-y-4">
        <TabsList className="bg-white border border-[hsl(220,15%,90%)]">
          <TabsTrigger value="dashboard">Dashboard</TabsTrigger>
          <TabsTrigger value="voyage-survey">Voyage Survey</TabsTrigger>
          <TabsTrigger value="monthly">Monthly Review</TabsTrigger>
        </TabsList>

        <TabsContent value="dashboard">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-6">
            <div className="portal-card p-6 text-center md:col-span-1">
              <Star className="w-8 h-8 text-amber-400 fill-amber-400 mx-auto mb-2" />
              <p className="text-4xl font-bold text-[hsl(215,25%,17%)]">{overallScore}</p>
              <p className="text-xs text-[hsl(215,12%,50%)]">Overall Score / 5</p>
              <p className="text-[10px] text-[hsl(215,12%,50%)] mt-1">{surveys.length} surveys</p>
            </div>
            <div className="portal-card p-4 md:col-span-3">
              <h3 className="font-bold text-sm mb-2">Score Trend</h3>
              {trends.length > 0 ? (
                <ResponsiveContainer width="100%" height={160}>
                  <LineChart data={trends}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis dataKey="month" tick={{ fontSize: 10 }} />
                    <YAxis domain={[3, 5]} tick={{ fontSize: 10 }} />
                    <Tooltip />
                    <Line type="monotone" dataKey="overall" stroke="#0066CC" strokeWidth={2.5} dot={{ r: 3 }} name="Overall" />
                    <Line type="monotone" dataKey="crew" stroke="#28A745" strokeWidth={1} dot={false} name="Crew" />
                    <Line type="monotone" dataKey="vessel" stroke="#FFC107" strokeWidth={1} dot={false} name="Vessel" />
                    <Line type="monotone" dataKey="punctuality" stroke="#DC3545" strokeWidth={1} dot={false} name="Punctuality" />
                    <Line type="monotone" dataKey="communication" stroke="#9333ea" strokeWidth={1} dot={false} name="Comms" />
                  </LineChart>
                </ResponsiveContainer>
              ) : (
                <p className="text-sm text-[hsl(215,12%,50%)] text-center py-8">No survey data yet</p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="portal-card p-4">
              <h3 className="font-bold text-sm mb-3">Per-Vessel Breakdown</h3>
              {vesselScores.length > 0 ? (
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={vesselScores} layout="vertical">
                    <XAxis type="number" domain={[0, 5]} tick={{ fontSize: 10 }} />
                    <YAxis type="category" dataKey="vessel" tick={{ fontSize: 10 }} width={100} />
                    <Tooltip />
                    <Bar dataKey="score" fill="#0066CC" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : (
                <p className="text-sm text-[hsl(215,12%,50%)] text-center py-8">No data</p>
              )}
            </div>
            <div className="portal-card p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-bold text-sm">Recent Feedback</h3>
                <span className="text-[10px] text-[hsl(215,12%,50%)]">{surveys.length} responses</span>
              </div>
              <div className="space-y-2 max-h-[200px] overflow-y-auto">
                {surveys.slice(0, 10).map((c: any) => (
                  <div key={c.id} className="p-2.5 rounded-lg bg-[hsl(220,20%,97%)]">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-[10px] font-bold text-[hsl(211,100%,40%)]">{c.voyage_ref}</span>
                      <span className="text-[10px] text-[hsl(215,12%,50%)]">{c.vessel_name}</span>
                      <div className="flex ml-auto">
                        {Array(c.rating_overall || 0).fill(0).map((_: any, i: number) => (
                          <Star key={i} className="w-3 h-3 text-amber-400 fill-amber-400" />
                        ))}
                      </div>
                    </div>
                    {c.comments && <p className="text-xs text-[hsl(215,25%,17%)]">{c.comments}</p>}
                    <p className="text-[9px] text-[hsl(215,12%,50%)] mt-1">
                      {new Date(c.submitted_at).toLocaleDateString()}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="voyage-survey">
          <div className="portal-card p-6 max-w-lg">
            <h2 className="font-bold text-lg mb-4">Post-Voyage Survey</h2>
            <div className="divide-y divide-[hsl(220,15%,90%)]">
              <StarRating field="overall" label="Overall Voyage Rating" />
              <StarRating field="crew" label="Crew Professionalism" />
              <StarRating field="vessel" label="Vessel Comfort & Cleanliness" />
              <StarRating field="punctuality" label="Punctuality / On-Time" />
              <StarRating field="communication" label="Communication from Ops" />
              <StarRating field="safety" label="Safety Impression" />
            </div>
            <div className="mt-4">
              <label className="text-xs font-medium text-[hsl(215,12%,50%)] mb-1 block">Comments</label>
              <textarea className="w-full border border-[hsl(220,15%,90%)] rounded-lg p-3 text-sm min-h-[80px]" placeholder="Any feedback..." />
            </div>
            <Button className="mt-4 bg-[hsl(211,100%,40%)] hover:bg-[hsl(211,100%,35%)] text-white" onClick={() => toast({ title: "Survey Submitted", description: "Thank you for your feedback!" })}>Submit Survey</Button>
          </div>
        </TabsContent>

        <TabsContent value="monthly">
          <div className="portal-card p-6 max-w-lg">
            <h2 className="font-bold text-lg mb-4">Monthly Service Review</h2>
            <div className="mb-4">
              <label className="text-xs font-medium text-[hsl(215,12%,50%)] mb-2 block">How likely are you to recommend Velocity Offshore? (0–10)</label>
              <div className="flex gap-1">
                {Array.from({ length: 11 }, (_, i) => (
                  <button key={i} className="w-8 h-8 rounded border border-[hsl(220,15%,90%)] text-xs font-medium hover:bg-[hsl(211,100%,40%)] hover:text-white transition-colors">{i}</button>
                ))}
              </div>
            </div>
            <div className="space-y-3">
              <div><label className="text-xs font-medium text-[hsl(215,12%,50%)] mb-1 block">What's working well?</label><textarea className="w-full border border-[hsl(220,15%,90%)] rounded-lg p-3 text-sm min-h-[60px]" /></div>
              <div><label className="text-xs font-medium text-[hsl(215,12%,50%)] mb-1 block">What could improve?</label><textarea className="w-full border border-[hsl(220,15%,90%)] rounded-lg p-3 text-sm min-h-[60px]" /></div>
            </div>
            <Button className="mt-4 bg-[hsl(211,100%,40%)] hover:bg-[hsl(211,100%,35%)] text-white" onClick={() => toast({ title: "Review Submitted" })}>Submit Review</Button>
          </div>
        </TabsContent>
      </Tabs>
    </PortalLayout>
  );
};

export default ClientSatisfaction;
